import { officePathByBrand } from '@aspendental/shared-components-web';
import { convertNameToUrlFormat } from '@aspendental/shared-components-web/lib/utils/convertNameToUrlFormat/convertNameToUrlFormat';

export type FaqItem = { question: string; answer: string };

const richTextToPlainText = (node: any): string => {
	if (!node) return '';
	if (node.nodeType === 'text') return node.value || '';
	if (Array.isArray(node.content)) return node.content.map(richTextToPlainText).join('');
	return '';
};

export const extractFaqItemsFromContents = (contentsItems: any[]): FaqItem[] =>
	contentsItems
		.filter((item: any) => item?.__typename === 'Accordions')
		.flatMap((accordion: any) => accordion?.accordionsCollection?.items ?? [])
		.filter((item: any) => item?.__typename === 'ContentTypeRichText' && item?.title)
		.map((item: any) => ({
			question: item.title as string,
			answer: richTextToPlainText(item?.content?.json),
		}))
		.filter(({ question, answer }) => question && answer);

export const generateProviderSchemaObject = (
	config: any,
	doctorDetails: any,
	path: any,
	pageTitle: string,
	pageDesc: string,
	faqItems?: FaqItem[]
) => {
	const { siteURL, features } = config as BrandThemeConfig;
	const schema = features?.schema as any;
	const { firstName, lastName } = doctorDetails.provider;
	const mainFacilityLocation = doctorDetails.provider.providerLocations?.find(({ isMain }) => isMain);
	const providerPhotoUrl = doctorDetails.providerPicByNpi?.[0]?.url || '';
	const providerType = features?.providerSchema?.['@type'] || ['Person', 'IndividualPhysician'];
	const providerName = `${firstName} ${lastName}`;
	const hasFaq = !!faqItems?.length;

	const address1 = mainFacilityLocation?.facility?.location?.address1;
	const city = mainFacilityLocation?.facility?.location?.city;
	const stateCode = mainFacilityLocation?.facility?.location?.stateCode;
	const zipCode = mainFacilityLocation?.facility?.address?.zipCode;
	const fullAddress = `${address1 || ''}, ${city || ''}, ${stateCode || ''} ${zipCode || ''}`;
	const googleMapsUrl = mainFacilityLocation
		? config.googleMapBusinessQuery.replace(/\[address\]/gi, `${fullAddress}`)
		: '';

	const officeDetailsUrl = `${siteURL}/${features.otherApps.facilityDetailsBasePath}/${officePathByBrand(
		config.themeId,
		{
			stateStrOptions: {
				stateAbbreviation: stateCode || '',
				state: '',
				city: city || '',
			},
			cityAddressOptions: {
				city: city ?? undefined,
				address: address1 ?? undefined,
				zip: zipCode ?? undefined,
			},
		}
	)}/`;

	const providerDetailsUrl = `${siteURL}${features.providers.root}${convertNameToUrlFormat(
		`${firstName} ${lastName}`
	)}${path}`;

	const schemaObject: {
		[key: string]: string[] | object | boolean | number | string;
	} = {
		name: `${firstName} ${lastName}, ${doctorDetails.provider.degreeName || ''}`,
		image: providerPhotoUrl,
		description: doctorDetails.provider.biography || '',
		hasCredential: {
			'@type': 'EducationalOccupationalCredential',
			name: doctorDetails.provider.degreeName || '',
		},
		url: providerDetailsUrl ?? '',
		usNPI: doctorDetails.provider.npi,
		isAcceptingNewPatients: true,
	};

	if (mainFacilityLocation) {
		schemaObject.practicesAt = {
			name: `${config.name} ${mainFacilityLocation?.facility?.name || ''}`,
			url: officeDetailsUrl ?? '',
			address: {
				'@type': 'PostalAddress',
				streetAddress: address1 || '',
				addressLocality: city || '',
				addressRegion: stateCode || '',
				postalCode: zipCode || '',
				addressCountry: 'US',
			},
			hasMap: googleMapsUrl,
			geo: {
				'@type': 'GeoCoordinates',
				latitude: mainFacilityLocation?.facility?.location?.latitude || '',
				longitude: mainFacilityLocation?.facility?.location?.longitude || '',
			},
			telephone: mainFacilityLocation?.facility?.technicalInfrastructure?.phoneNumber || '',
			email: mainFacilityLocation?.facility?.technicalInfrastructure?.email || '',
		};

		if (features?.providerSchema?.practicesAt?.['@type']) {
			schemaObject.practicesAt['@type'] = features.providerSchema.practicesAt['@type'];
		}
	}

	schemaObject['@type'] = providerType;
	schemaObject['@id'] = `${providerDetailsUrl}#provider`;
	schemaObject['worksFor'] = { '@id': `${siteURL}/#organization` };

	if (features?.providerSchema?.medicalSpecialty) {
		schemaObject.medicalSpecialty = features.providerSchema.medicalSpecialty;
	}

	const websiteNode = {
		'@type': 'WebSite',
		'@id': `${siteURL}/#website`,
		url: `${siteURL}/`,
		name: schema?.isPartOf?.name || '',
		inLanguage: 'en-US',
		publisher: { '@id': `${siteURL}/#organization` },
	};

	const organizationNode = {
		'@type': 'MedicalOrganization',
		'@id': `${siteURL}/#organization`,
		url: schema?.publisher?.url || siteURL,
		name: schema?.publisher?.name || '',
		logo: schema?.publisher?.logo,
		address: {
			...schema?.publisher?.address,
			addressCountry: 'US',
		},
		sameAs: schema?.sameAs || [],
	};

	const webPageNode: { [key: string]: string | string[] | object } = {
		'@type': hasFaq ? ['WebPage', 'FAQPage'] : 'WebPage',
		'@id': `${providerDetailsUrl}#webpage`,
		url: providerDetailsUrl,
		name: pageTitle,
		description: pageDesc,
		inLanguage: 'en-US',
		isPartOf: { '@id': `${siteURL}/#website` },
		breadcrumb: { '@id': `${providerDetailsUrl}#breadcrumb` },
		about: {
			'@type': providerType,
			name: providerName,
		},
	};

	if (providerPhotoUrl) {
		webPageNode.primaryImageOfPage = providerPhotoUrl;
	}

	if (hasFaq) {
		webPageNode.mainEntity = faqItems!.map(({ question, answer }) => ({
			'@type': 'Question',
			name: question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: answer,
			},
		}));
	}

	const scheduleActionNode = {
		'@type': 'ScheduleAction',
		'@id': `${providerDetailsUrl}#schedule-consult`,
		name: 'Schedule a Free Consultation',
		target: {
			urlTemplate: `${siteURL}/schedule-a-consultation/`,
		},
		agent: {
			'@type': providerType,
			name: providerName,
		},
	};

	const graph: object[] = [websiteNode, organizationNode, webPageNode, schemaObject, scheduleActionNode];

	return {
		'@context': 'https://schema.org',
		'@graph': graph,
	};
};
